import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listLatestReceivedAnnouncements,
  listPendingAnnouncements,
  updateAnnouncementAcknowledged,
  updateAnnouncementDismissed,
  updateAnnouncementSeen,
} from '@/app/modules/announcements/core/_requests';
import { RECEIVED_ANNOUNCEMENTS_TABLE_KEY } from '@/app/modules/announcements/core/_consts';
import { sortOldestFirst } from '@/app/modules/announcements/core/_utils';
import { getDataTableQueryKey } from '@/app/modules/table/DataTable';

export const ANNOUNCEMENTS_QUERY_KEY: QueryKey = ['announcements'];
export const PENDING_ANNOUNCEMENTS_QUERY_KEY: QueryKey = [...ANNOUNCEMENTS_QUERY_KEY, 'pending'];
const LATEST_RECEIVED_QUERY_KEY: QueryKey = [...ANNOUNCEMENTS_QUERY_KEY, 'latest-received'];
// O modal de entrada fica no layout: invalidar toda DataTable recarregaria a tabela de qualquer tela aberta.
const RECEIVED_TABLE_QUERY_KEY: QueryKey = getDataTableQueryKey(RECEIVED_ANNOUNCEMENTS_TABLE_KEY);

export const usePendingAnnouncements = () =>
  useQuery({
    queryKey: PENDING_ANNOUNCEMENTS_QUERY_KEY,
    queryFn: async () => {
      const { data } = await listPendingAnnouncements();
      return sortOldestFirst(data);
    },
    // O padrão do app desliga; voltar à aba é um dos momentos de conferir os pendentes.
    refetchOnWindowFocus: true,
    meta: { suppressGlobalError: true },
  });

export const useLatestReceivedAnnouncements = () =>
  useQuery({
    queryKey: LATEST_RECEIVED_QUERY_KEY,
    queryFn: async () => {
      const { data } = await listLatestReceivedAnnouncements();
      return data.content;
    },
    meta: { suppressGlobalError: true },
  });

/** Recarrega pendentes, últimos recebidos e a tabela de recebidos depois de uma ação de quem recebe. */
export const useRefreshAnnouncements = () => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ANNOUNCEMENTS_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: RECEIVED_TABLE_QUERY_KEY });
  };
};

const useReceiptMutation = (request: (id: string) => Promise<unknown>) => {
  const refreshAnnouncements = useRefreshAnnouncements();
  return useMutation({ mutationFn: request, onSuccess: refreshAnnouncements });
};

export const useMarkAnnouncementSeen = () => useReceiptMutation(updateAnnouncementSeen);
export const useAcknowledgeAnnouncement = () => useReceiptMutation(updateAnnouncementAcknowledged);
export const useDismissAnnouncement = () => useReceiptMutation(updateAnnouncementDismissed);
